// Who may change pawn records already on file, and the wrapper that makes a
// block of controls view-only for everybody else.
//
// The rule is utils/roles.js canEditPawnRecords: Store Head, Store HOD and the
// service roles may edit an existing payment / receipt / settlement and the pawn
// settings; Store Staff may only raise new ones and view the rest. The backend
// refuses the same edits (utils/pawnEditGuard.js), so hiding a control here is
// about not offering what would fail, not about security.
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { canEditPawnRecords } from "../../utils/roles";

export const VIEW_ONLY_MESSAGE =
  "View only — only the Store Head or Store HOD can edit";

// Marks the inputs useViewOnlyGuard made read-only, so it unlocks only those.
const LOCK_ATTR = "data-view-only-lock";

/** True when the signed-in role may edit existing pawn records / settings. */
export const usePawnEditAccess = () => {
  const { role, userInfo } = useContext(AutoTaskAuthContext) || {};
  return canEditPawnRecords(role ?? userInfo?.role);
};

// Everything a person could change a value with. Scroll containers are NOT in
// this list on purpose: a wheel over a control with pointer-events off lands on
// the list behind it, so the scheme / slab lists still scroll. (`inert` would
// have been simpler, but it takes the scroll areas down with it.)
const CONTROL_SELECTOR = [
  "input",
  "textarea",
  "select",
  "button",
  "label",
  "a",
  "[role='button']",
  "[role='combobox']",
  "[role='checkbox']",
  "[role='switch']",
  "[role='slider']",
  "[role='radio']",
  "[tabindex]",
  ".MuiSelect-select",
  ".MuiInputBase-root",
  ".MuiSwitch-root",
  ".MuiCheckbox-root",
  ".MuiSlider-root",
  ".MuiToggleButton-root",
].join(", ");

const VIEW_ONLY_SX = {
  [`& ${CONTROL_SELECTOR.split(", ").join(", & ")}`]: {
    pointerEvents: "none",
  },
  "& .MuiInputBase-root, & .MuiSwitch-root, & .MuiCheckbox-root": {
    opacity: 0.85,
  },
};

/**
 * Props that make an element's contents untouchable while `readOnly` is set:
 * controls stop taking the pointer, nothing inside keeps focus, inputs are
 * `readonly`, and no keystroke or paste reaches them. Scrolling still works.
 * Spread them on any MUI element that forwards a ref (a Grid item, a Box) —
 * `ref` and `sx` included — or use ViewOnlyZone.
 */
export const useViewOnlyGuard = (readOnly) => {
  const block = useCallback(
    (e) => {
      if (!readOnly) return;
      e.preventDefault();
      e.stopPropagation();
    },
    [readOnly]
  );
  const dropFocus = useCallback(
    (e) => {
      if (readOnly && typeof e.target?.blur === "function") e.target.blur();
    },
    [readOnly]
  );
  const blockKeys = useCallback(
    (e) => {
      // Tab still moves past the block, so keyboard users are not trapped.
      if (readOnly && e.key !== "Tab") block(e);
    },
    [readOnly, block]
  );

  // The handlers above stop the pointer and the keyboard, but text can still
  // arrive without a keydown (IME, autofill, dictation). So every input inside
  // is also made genuinely `readonly` while locked — including ones a section
  // renders later, hence the observer. Only fields this hook locked are
  // unlocked again; a field the screen made read-only itself is left alone.
  const [node, setNode] = useState(null);
  useEffect(() => {
    if (!node || !readOnly) return undefined;
    const lock = () =>
      node.querySelectorAll("input, textarea").forEach((el) => {
        if (el.readOnly) return;
        el.readOnly = true;
        el.setAttribute(LOCK_ATTR, "");
      });
    lock();
    const observer = new MutationObserver(lock);
    observer.observe(node, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      node.querySelectorAll(`[${LOCK_ATTR}]`).forEach((el) => {
        el.readOnly = false;
        el.removeAttribute(LOCK_ATTR);
      });
    };
  }, [node, readOnly]);

  return {
    ref: setNode,
    "aria-readonly": readOnly || undefined,
    onFocusCapture: dropFocus,
    onKeyDownCapture: blockKeys,
    onPasteCapture: block,
    onDropCapture: block,
    onMouseDownCapture: block,
    onClickCapture: block,
    sx: readOnly ? VIEW_ONLY_SX : undefined,
  };
};

/** A Box wearing useViewOnlyGuard. With `readOnly` false it is a plain Box. */
export const ViewOnlyZone = ({ readOnly, children, sx, ...rest }) => {
  const { sx: guardSx, ...guard } = useViewOnlyGuard(readOnly);
  return (
    <Box
      {...guard}
      sx={[guardSx, ...(Array.isArray(sx) ? sx : [sx])].filter(Boolean)}
      {...rest}
    >
      {children}
    </Box>
  );
};
